import type {
  UploadCheckpoint,
  UploadFailure,
  UploadFailureCode,
} from "./uploadJobContract";

export type UploadFailurePresentation = {
  titleKey: string;
  descriptionKey: string;
  statusKey: string;
  actionKey: string;
  checkpointKey: string;
  retryable: boolean;
};

/**
 * The server sends a stable code, not user-facing prose. Keeping this map in
 * the shared ingestion package means every upload surface can resolve the
 * same failure into the same action and localized copy.
 */
export const UPLOAD_FAILURE_PRESENTATION: Readonly<
  Record<UploadFailureCode, UploadFailurePresentation>
> = {
  unsupported_type: {
    titleKey: "documents.upload.failure.unsupported_type.title",
    descriptionKey: "documents.upload.failure.unsupported_type.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  file_too_large: {
    titleKey: "documents.upload.failure.file_too_large.title",
    descriptionKey: "documents.upload.failure.file_too_large.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  batch_too_many_files: {
    titleKey: "documents.upload.failure.batch_too_many_files.title",
    descriptionKey: "documents.upload.failure.batch_too_many_files.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  batch_too_large: {
    titleKey: "documents.upload.failure.batch_too_large.title",
    descriptionKey: "documents.upload.failure.batch_too_large.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  empty_file: {
    titleKey: "documents.upload.failure.empty_file.title",
    descriptionKey: "documents.upload.failure.empty_file.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  corrupt_file: {
    titleKey: "documents.upload.failure.corrupt_file.title",
    descriptionKey: "documents.upload.failure.corrupt_file.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  password_protected: {
    titleKey: "documents.upload.failure.password_protected.title",
    descriptionKey: "documents.upload.failure.password_protected.description",
    statusKey: "documents.upload.failure.status.not_uploaded",
    actionKey: "documents.upload.failure.choose_another",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  duplicate: {
    titleKey: "documents.upload.failure.duplicate.title",
    descriptionKey: "documents.upload.failure.duplicate.description",
    statusKey: "documents.upload.failure.status.already_saved",
    actionKey: "documents.upload.failure.use_saved_file",
    checkpointKey: "documents.upload.failure.checkpoint.validation",
    retryable: false,
  },
  network_stalled: {
    titleKey: "documents.upload.failure.network_stalled.title",
    descriptionKey: "documents.upload.failure.network_stalled.description",
    statusKey: "documents.upload.failure.status.not_saved",
    actionKey: "documents.upload.failure.retry_upload",
    checkpointKey: "documents.upload.failure.checkpoint.upload",
    retryable: true,
  },
  upload_failed: {
    titleKey: "documents.upload.failure.upload_failed.title",
    descriptionKey: "documents.upload.failure.upload_failed.description",
    statusKey: "documents.upload.failure.status.not_saved",
    actionKey: "documents.upload.failure.retry_upload",
    checkpointKey: "documents.upload.failure.checkpoint.upload",
    retryable: true,
  },
  extraction_failed: {
    titleKey: "documents.upload.failure.extraction_failed.title",
    descriptionKey: "documents.upload.failure.extraction_failed.description",
    statusKey: "documents.upload.failure.status.saved_not_read",
    actionKey: "documents.upload.failure.retry_reading",
    checkpointKey: "documents.upload.failure.checkpoint.processing",
    retryable: true,
  },
  persistence_failed: {
    titleKey: "documents.upload.failure.persistence_failed.title",
    descriptionKey: "documents.upload.failure.persistence_failed.description",
    statusKey: "documents.upload.failure.status.extracted_not_saved",
    actionKey: "documents.upload.failure.retry_saving",
    checkpointKey: "documents.upload.failure.checkpoint.saving",
    retryable: true,
  },
  canceled: {
    titleKey: "documents.upload.failure.canceled.title",
    descriptionKey: "documents.upload.failure.canceled.description",
    statusKey: "documents.upload.failure.status.canceled",
    actionKey: "documents.upload.failure.dismiss",
    checkpointKey: "documents.upload.failure.checkpoint.upload",
    retryable: false,
  },
};

export function getUploadFailurePresentation(
  failure: Pick<UploadFailure, "code" | "retryable"> | UploadFailureCode,
): UploadFailurePresentation {
  const code = typeof failure === "string" ? failure : failure.code;
  const presentation = UPLOAD_FAILURE_PRESENTATION[code];

  // A retry flag from the backend can only narrow the client policy. This
  // prevents a malformed response from making validation failures retryable.
  if (typeof failure === "string" || failure.retryable) return presentation;
  return { ...presentation, retryable: false };
}

export function isUploadFailureRetryable(failure: UploadFailure): boolean {
  return getUploadFailurePresentation(failure).retryable;
}

export function uploadCheckpointKey(checkpoint: UploadCheckpoint): string {
  return `documents.upload.failure.checkpoint.${checkpoint}`;
}
